import { Fragment, useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { parseMarkdown, type Block, type Inline, type ListItem } from '../../lib/markdown';

/**
 * An assistant reply drawn from its Markdown (lib/markdown.ts): headings, bold, lists, tables, links and code. Every
 * piece of text goes through React as text, so nothing in a reply becomes HTML. A portal path opens in the portal;
 * any other link opens in a new tab with no referrer.
 */
export function Markdown({ text, onNavigate }: { text: string; onNavigate?: () => void }) {
  const blocks = useMemo(() => parseMarkdown(text), [text]);
  return <div className="ap-md">{renderBlocks(blocks, onNavigate)}</div>;
}

function renderInline(nodes: Inline[], onNavigate?: () => void): ReactNode[] {
  return nodes.map((n, i) => {
    switch (n.t) {
      case 'text':
        return <Fragment key={i}>{n.v}</Fragment>;
      case 'br':
        return <br key={i} />;
      case 'code':
        return <code key={i}>{n.v}</code>;
      case 'strong':
        return <strong key={i}>{renderInline(n.c, onNavigate)}</strong>;
      case 'em':
        return <em key={i}>{renderInline(n.c, onNavigate)}</em>;
      case 'del':
        return <del key={i}>{renderInline(n.c, onNavigate)}</del>;
      case 'link':
        return n.internal ? (
          <Link key={i} to={n.href} onClick={onNavigate}>
            {renderInline(n.c, onNavigate)}
          </Link>
        ) : (
          <a key={i} href={n.href} target="_blank" rel="noopener noreferrer nofollow">
            {renderInline(n.c, onNavigate)}
          </a>
        );
    }
  });
}

function renderItems(items: ListItem[], onNavigate?: () => void) {
  return items.map((it, i) => (
    <li key={i}>
      {renderInline(it.c, onNavigate)}
      {it.sub.length ? renderBlocks(it.sub, onNavigate) : null}
    </li>
  ));
}

function renderBlocks(blocks: Block[], onNavigate?: () => void): ReactNode[] {
  return blocks.map((b, i) => {
    switch (b.t) {
      case 'h': {
        // Reply headings sit inside the panel, so they start at h3 and stay small.
        const level = Math.min(6, Math.max(3, b.level + 2));
        const Tag = `h${level}` as 'h3' | 'h4' | 'h5' | 'h6';
        return <Tag key={i}>{renderInline(b.c, onNavigate)}</Tag>;
      }
      case 'p':
        return <p key={i}>{renderInline(b.c, onNavigate)}</p>;
      case 'ul':
        return <ul key={i}>{renderItems(b.items, onNavigate)}</ul>;
      case 'ol':
        return (
          <ol key={i} start={b.start === 1 ? undefined : b.start}>
            {renderItems(b.items, onNavigate)}
          </ol>
        );
      case 'code':
        return (
          <pre key={i} data-lang={b.lang || undefined}>
            <code>{b.v}</code>
          </pre>
        );
      case 'quote':
        return <blockquote key={i}>{renderBlocks(b.c, onNavigate)}</blockquote>;
      case 'hr':
        return <hr key={i} />;
      case 'table':
        return (
          <div key={i} className="ap-md-table">
            <table>
              <thead>
                <tr>
                  {b.head.map((c, k) => (
                    <th key={k} style={b.align[k] ? { textAlign: b.align[k]! } : undefined}>
                      {renderInline(c, onNavigate)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.rows.map((r, j) => (
                  <tr key={j}>
                    {r.map((c, k) => (
                      <td key={k} style={b.align[k] ? { textAlign: b.align[k]! } : undefined}>
                        {renderInline(c, onNavigate)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
    }
  });
}
